import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Clock, Search, QrCode, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ── QR Code using Google Charts API (no npm install needed) ──
function QRCode({ value, size = 180 }) {
  const encoded = encodeURIComponent(value);
  const url = `https://chart.googleapis.com/chart?chs=${size}x${size}&cht=qr&chl=${encoded}&choe=UTF-8`;
  return (
    <img
      src={url}
      alt="QR Code"
      width={size}
      height={size}
      className="rounded-xl border border-gray-200"
      style={{ imageRendering: 'pixelated' }}
    />
  );
}

// ── Status badge ──
function StatusBadge({ label, color }) {
  const colors = {
    green:  'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red:    'bg-red-100 text-red-700 border-red-200',
    blue:   'bg-blue-100 text-blue-800 border-blue-200',
    gray:   'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-sans font-bold border ${colors[color] || colors.gray}`}>
      {label}
    </span>
  );
}

function formatDate(dateStr, lang) {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);
}

// ── Main verification result card ──
function VerificationResult({ data, code }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const verifyUrl = `${window.location.origin}/verify/${code}`;

  const handlePrint = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Verifikasi Booking – ${data.booking_code}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;padding:24px;color:#111}
  .header{border-bottom:3px solid #562b18;padding-bottom:12px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-end}
  .brand{font-size:20px;font-weight:700;color:#562b18}
  .sub{font-size:11px;color:#6b7280}
  .status-ok{background:#dcfce7;color:#166534;padding:8px 16px;border-radius:8px;font-weight:700;font-size:14px;display:inline-block;margin-bottom:16px}
  .status-bad{background:#fee2e2;color:#991b1b;padding:8px 16px;border-radius:8px;font-weight:700;font-size:14px;display:inline-block;margin-bottom:16px}
  .code{font-size:22px;font-weight:700;letter-spacing:2px;color:#562b18;margin-bottom:16px}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px}
  td{padding:6px 0;border-bottom:1px solid #f3f4f6}
  td:first-child{color:#6b7280;width:40%}
  td:last-child{font-weight:600}
  .footer{font-size:10px;color:#9ca3af;text-align:center;margin-top:16px}
  .qr{text-align:center;margin:12px 0}
</style></head><body>
<div class="header">
  <div><div class="brand">Ndalem Mataram</div><div class="sub">Jl. K.H. Agus Salim No.18, Banyuwangi</div></div>
  <div class="sub">Verifikasi: ${new Date().toLocaleString('id-ID')}</div>
</div>
<div class="${data.valid ? 'status-ok' : 'status-bad'}">${data.valid ? '✓ BOOKING VALID & LUNAS' : '✗ BOOKING TIDAK VALID'}</div>
<div class="code">${data.booking_code}</div>
<table>
  <tr><td>Nama Tamu</td><td>${data.guest_name}</td></tr>
  <tr><td>Tipe Kamar</td><td>${data.room_type}</td></tr>
  <tr><td>Check-in</td><td>${new Date(data.checkin_date).toLocaleDateString('id-ID',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</td></tr>
  <tr><td>Check-out</td><td>${new Date(data.checkout_date).toLocaleDateString('id-ID',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</td></tr>
  <tr><td>Jumlah Malam</td><td>${data.num_nights} malam</td></tr>
  <tr><td>Jumlah Tamu</td><td>${data.num_guests} orang</td></tr>
  <tr><td>Status Booking</td><td>${data.booking_status_label}</td></tr>
  <tr><td>Status Pembayaran</td><td>${data.payment_status_label}</td></tr>
  <tr><td>Total</td><td>${new Intl.NumberFormat('id-ID',{style:'currency',currency:'IDR',maximumFractionDigits:0}).format(data.total_price)}</td></tr>
</table>
<div class="footer">Diverifikasi dari ${verifyUrl} — Ndalem Mataram © ${new Date().getFullYear()}</div>
<script>window.onload=()=>window.print()</script>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Big status banner */}
      <div className={`rounded-2xl p-6 flex items-start gap-4 ${
        data.valid
          ? 'bg-green-50 border-2 border-green-300'
          : data.is_cancelled
          ? 'bg-red-50 border-2 border-red-300'
          : 'bg-yellow-50 border-2 border-yellow-300'
      }`}>
        <div className="shrink-0 mt-0.5">
          {data.valid
            ? <CheckCircle size={36} className="text-green-500" />
            : data.is_cancelled
            ? <XCircle size={36} className="text-red-500" />
            : <AlertCircle size={36} className="text-yellow-500" />}
        </div>
        <div>
          <p className={`font-display text-xl font-bold mb-1 ${
            data.valid ? 'text-green-800' : data.is_cancelled ? 'text-red-800' : 'text-yellow-800'
          }`}>
            {data.valid
              ? lang === 'id' ? 'Booking Valid & Lunas ✓' : 'Valid & Paid Booking ✓'
              : data.is_cancelled
              ? lang === 'id' ? 'Booking Dibatalkan ✗' : 'Booking Cancelled ✗'
              : lang === 'id' ? 'Belum Lunas — Perlu Konfirmasi' : 'Not Yet Paid — Needs Confirmation'}
          </p>
          <p className="text-sm font-sans text-gray-600">{data.message}</p>
        </div>
      </div>

      {/* Main details grid */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Code header */}
        <div className="bg-primary-950 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-white/50 text-xs font-sans uppercase tracking-widest mb-0.5">
              {lang === 'id' ? 'Kode Booking' : 'Booking Code'}
            </p>
            <p className="font-display text-2xl text-white font-bold tracking-wider">{data.booking_code}</p>
          </div>
          <div className="flex gap-2">
            <StatusBadge label={data.booking_status_label} color={data.booking_status_color} />
            <StatusBadge label={data.payment_status_label} color={data.payment_status_color} />
          </div>
        </div>

        {/* Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {[
              { label: lang === 'id' ? 'Nama Tamu' : 'Guest Name', value: data.guest_name, big: true },
              { label: lang === 'id' ? 'Tipe Kamar' : 'Room Type', value: data.room_type },
              { label: 'Check-in', value: formatDate(data.checkin_date, lang) },
              { label: 'Check-out', value: formatDate(data.checkout_date, lang) },
              { label: lang === 'id' ? 'Jumlah Malam' : 'Nights', value: `${data.num_nights} ${lang === 'id' ? 'malam' : 'nights'}` },
              { label: lang === 'id' ? 'Jumlah Tamu' : 'Guests', value: `${data.num_guests} ${lang === 'id' ? 'orang' : 'guests'}` },
            ].map(({ label, value, big }) => (
              <div key={label} className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-400 font-sans uppercase tracking-wide mb-0.5">{label}</p>
                <p className={`font-sans font-semibold text-dark ${big ? 'text-base' : 'text-sm'}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="bg-primary-50 border border-primary-100 rounded-xl px-5 py-4 flex justify-between items-center mb-6">
            <div>
              <p className="text-xs text-gray-400 font-sans uppercase tracking-wide">
                {lang === 'id' ? 'Total Pembayaran' : 'Total Payment'}
              </p>
              <p className="font-display text-2xl text-primary-700 font-bold">{formatCurrency(data.total_price)}</p>
            </div>
            <StatusBadge label={data.payment_status_label} color={data.payment_status_color} />
          </div>

          {/* QR + actions */}
          <div className="flex flex-col sm:flex-row items-center gap-6 pt-4 border-t border-gray-100">
            <div className="flex flex-col items-center gap-2">
              <QRCode value={verifyUrl} size={160} />
              <p className="text-xs text-gray-400 font-sans text-center max-w-[160px]">
                {lang === 'id' ? 'Scan untuk verifikasi ulang' : 'Scan to re-verify'}
              </p>
            </div>
            <div className="flex-1 space-y-3">
              <p className="text-xs text-gray-500 font-sans">
                {lang === 'id'
                  ? 'QR code ini mengarah langsung ke halaman verifikasi. Scan dengan kamera HP untuk memastikan keaslian voucher.'
                  : 'This QR code links directly to this verification page. Scan with your phone camera to confirm voucher authenticity.'}
              </p>
              <div className="text-xs font-mono text-gray-400 bg-gray-50 rounded-lg px-3 py-2 break-all">
                {verifyUrl}
              </div>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white text-xs font-sans font-semibold rounded-xl transition-colors"
              >
                <Printer size={13} />
                {lang === 'id' ? 'Cetak Hasil Verifikasi' : 'Print Verification'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Verified by */}
      <p className="text-center text-xs text-gray-400 font-sans">
        {lang === 'id' ? 'Diverifikasi langsung dari database Ndalem Mataram' : 'Verified directly from Ndalem Mataram database'}
        {' · '}{new Date().toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')}
      </p>
    </div>
  );
}

// ── Not found state ──
function NotFound({ code, lang }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-red-200 shadow-sm p-8 text-center space-y-4">
      <XCircle size={52} className="text-red-400 mx-auto" />
      <div>
        <h2 className="font-display text-2xl text-dark font-bold mb-2">
          {lang === 'id' ? 'Kode Tidak Ditemukan' : 'Code Not Found'}
        </h2>
        <p className="text-gray-500 font-sans text-sm">
          {lang === 'id'
            ? `Kode "${code}" tidak ada dalam sistem kami. Voucher ini kemungkinan besar palsu.`
            : `Code "${code}" was not found in our system. This voucher is likely fraudulent.`}
        </p>
      </div>
      <div className="bg-red-50 rounded-xl px-5 py-4 text-sm font-sans text-red-700 text-left space-y-1">
        <p className="font-semibold">{lang === 'id' ? 'Kemungkinan penyebab:' : 'Possible reasons:'}</p>
        <p>• {lang === 'id' ? 'Kode diketik salah (perhatikan huruf I dan angka 1)' : 'Code typed incorrectly (check letter I vs number 1)'}</p>
        <p>• {lang === 'id' ? 'Voucher palsu / dipalsukan' : 'Fake or forged voucher'}</p>
        <p>• {lang === 'id' ? 'Booking dibuat di luar sistem website' : 'Booking made outside the website system'}</p>
      </div>
      <p className="text-xs text-gray-400 font-sans">
        {lang === 'id'
          ? 'Hubungi ndalem.mataram1@gmail.com atau WhatsApp +62 813-7857-5758 untuk konfirmasi manual.'
          : 'Contact ndalem.mataram1@gmail.com or WhatsApp +62 813-7857-5758 for manual confirmation.'}
      </p>
    </div>
  );
}

// ── Main Page ──
export default function VerifyBookingPage() {
  usePageTitle('Verifikasi Booking');
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [inputCode, setInputCode] = useState(urlCode || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Auto-search if code is in URL
  useEffect(() => {
    if (urlCode) {
      doVerify(urlCode);
    }
  }, [urlCode]);

  const doVerify = async (codeToCheck) => {
    const clean = codeToCheck.trim().toUpperCase();
    if (!clean) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setSearched(false);
    try {
      const res = await fetch(`${API}/bookings/verify/${encodeURIComponent(clean)}`);
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setResult(data);
      setSearched(true);
      // Update URL without reload
      window.history.replaceState(null, '', `/verify/${clean}`);
    } catch {
      setError(lang === 'id' ? 'Gagal menghubungi server. Coba lagi.' : 'Failed to reach server. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    doVerify(inputCode);
  };

  return (
    <div className="bg-cream min-h-screen">
      {/* Header */}
      <div className="bg-primary-950 pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4881c' fill-opacity='0.8'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="relative max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-5">
            <QrCode size={28} className="text-accent-400" />
          </div>
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-3">
            — {lang === 'id' ? 'Keaslian Voucher' : 'Voucher Authenticity'} —
          </p>
          <h1 className="font-display text-4xl md:text-5xl text-white font-bold mb-4">
            {lang === 'id' ? 'Verifikasi Booking' : 'Booking Verification'}
          </h1>
          <p className="text-white/60 font-sans text-base max-w-xl mx-auto">
            {lang === 'id'
              ? 'Masukkan kode booking dari voucher untuk memastikan keaslian dan status pembayarannya secara real-time.'
              : 'Enter the booking code from a voucher to verify its authenticity and payment status in real-time.'}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 space-y-8">
        {/* Search form */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <label className="block text-sm font-sans font-semibold text-dark mb-3">
            {lang === 'id' ? 'Kode Booking' : 'Booking Code'}
          </label>
          <form onSubmit={handleSearch} className="flex gap-3">
            <input
              value={inputCode}
              onChange={e => setInputCode(e.target.value.toUpperCase())}
              placeholder="NM-20260305-A3X"
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-mono text-sm text-dark focus:outline-none focus:border-primary-500 uppercase tracking-wider"
              disabled={loading}
              autoFocus={!urlCode}
            />
            <button
              type="submit"
              disabled={loading || !inputCode.trim()}
              className="flex items-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-300 text-white font-sans font-semibold rounded-xl transition-colors"
            >
              {loading
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Search size={16} />}
              {loading
                ? (lang === 'id' ? 'Memeriksa...' : 'Checking...')
                : (lang === 'id' ? 'Verifikasi' : 'Verify')}
            </button>
          </form>
          <p className="text-xs text-gray-400 font-sans mt-2">
            {lang === 'id'
              ? 'Format: NM-YYYYMMDD-XXX (contoh: NM-20260305-A3X)'
              : 'Format: NM-YYYYMMDD-XXX (example: NM-20260305-A3X)'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
            <AlertCircle size={20} className="text-red-500 shrink-0" />
            <p className="text-red-700 font-sans text-sm">{error}</p>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4 animate-pulse">
            <div className="h-20 bg-gray-100 rounded-xl" />
            <div className="h-48 bg-gray-100 rounded-xl" />
          </div>
        )}

        {/* Result */}
        {searched && result && !loading && (
          result.valid === false && result.booking_code === result.booking_code && !result.guest_name
            ? <NotFound code={inputCode} lang={lang} />
            : <VerificationResult data={result} code={result.booking_code} />
        )}

        {/* Info boxes — shown before any search */}
        {!searched && !loading && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: '🔐',
                title: lang === 'id' ? 'Langsung dari Database' : 'Direct from Database',
                desc: lang === 'id'
                  ? 'Data diambil real-time dari server kami, tidak bisa dipalsukan.'
                  : 'Data is fetched real-time from our server — cannot be forged.',
              },
              {
                icon: '⚡',
                title: lang === 'id' ? 'Instan & Akurat' : 'Instant & Accurate',
                desc: lang === 'id'
                  ? 'Status pembayaran dan booking terkini setiap saat.'
                  : 'Always shows the latest booking and payment status.',
              },
              {
                icon: '📱',
                title: lang === 'id' ? 'Scan QR di Voucher' : 'Scan QR from Voucher',
                desc: lang === 'id'
                  ? 'Setiap voucher punya QR yang langsung mengarah ke halaman ini.'
                  : 'Every voucher has a QR code linking directly to this page.',
              },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-display font-bold text-dark text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500 font-sans leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        )}

        {/* Staff note */}
        <div className="bg-primary-50 border border-primary-100 rounded-2xl p-5 flex gap-3">
          <Clock size={18} className="text-primary-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-sans font-semibold text-primary-800 mb-1">
              {lang === 'id' ? 'Panduan untuk Staff' : 'Staff Guide'}
            </p>
            <p className="text-xs text-primary-700 font-sans leading-relaxed">
              {lang === 'id'
                ? 'Saat tamu check-in: (1) Scan QR di voucher mereka, atau (2) ketik kode booking di kolom atas. Booking valid harus berstatus "Dikonfirmasi ✓" dan "Lunas ✓". Jika status kuning atau merah, hubungi reservasi sebelum melayani tamu.'
                : 'At guest check-in: (1) Scan the QR on their voucher, or (2) type the booking code above. A valid booking must show "Confirmed ✓" and "Paid ✓". If status is yellow or red, contact reservations before proceeding.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
